import React, { useState, useEffect, useCallback } from 'react';
import { auth, db } from './firebase';
import { signInWithCustomToken, signOut } from 'firebase/auth';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import './MobileCheckout.css';

// Same live gateway key used by the proven website checkout flow (ResidentDashboard.js)
const RAZORPAY_KEY = 'rzp_live_Sms01Pi653IXpu';
const API_BASE_URL = 'https://api.bathindahousingcomplex.com';

const PROPERTY_LABELS = {
  houses: 'House',
  shops: 'Shop',
  booths: 'Booth',
  c_buildings: 'Commercial Building',
  n_home: 'Nursing Home',
};

function formatPropertyType(type) {
  return PROPERTY_LABELS[type] || type;
}

function MobileCheckout() {
  // stage: loading -> ready -> confirm -> verifying -> success
  //        error / verify-error / payment-failed are terminal-ish states with a retry path
  const [stage, setStage] = useState('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [userRefInfo, setUserRefInfo] = useState({ propertyType: '', plotNo: '' });
  const [profileData, setProfileData] = useState(null);
  const [bills, setBills] = useState([]);
  const [selectedMonthlyIds, setSelectedMonthlyIds] = useState([]);
  const [selectedYearlyIds, setSelectedYearlyIds] = useState([]);
  const [checkoutData, setCheckoutData] = useState(null);
  const [busy, setBusy] = useState(false);

  const signalSuccessAndClose = useCallback(() => {
    // A real navigation (not client-side routing) is required so the Flutter
    // WebView's onNavigationRequest can intercept the URL and close the screen.
    const url = new URL(window.location.href);
    url.searchParams.set('payment-success', 'true');
    window.location.replace(url.toString());
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // If we've already been redirected here post-payment (or the app's WebView
    // somehow doesn't intercept in time), show a clean success screen directly.
    if (params.get('payment-success') === 'true') {
      setStage('success');
      return;
    }

    const token = params.get('token');
    const type = params.get('type');
    const plot = params.get('plot');

    if (!token || !type || !plot) {
      setErrorMsg('This checkout link is invalid or incomplete. Please go back and tap "Pay Bills" again in the app.');
      setStage('error');
      return;
    }

    setUserRefInfo({ propertyType: type, plotNo: plot });

    (async () => {
      try {
        // Always start from a clean session so a stale/leftover login in this
        // WebView can never be mixed up with the resident this token belongs to.
        if (auth.currentUser) {
          await signOut(auth);
        }
        await signInWithCustomToken(auth, token);
        await Promise.all([fetchProfile(type, plot), fetchBills(type, plot)]);
        setStage('ready');
      } catch (err) {
        setErrorMsg('Your checkout session has expired or is invalid. Please close this screen and tap "Pay Bills" again in the app.');
        setStage('error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async (type, plot) => {
    try {
      const snap = await getDoc(doc(db, type, plot));
      if (snap.exists()) setProfileData(snap.data());
    } catch (err) {
      // Non-critical — prefill/greeting only. Don't block checkout on this.
    }
  };

  const fetchBills = async (type, plot) => {
    const q = query(collection(db, 'bills'), where('houseNo', '==', plot), where('propertyType', '==', type));
    const snap = await getDocs(q);
    const fetched = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    fetched.sort((a, b) => (a.month || '').localeCompare(b.month || ''));
    setBills(fetched);
    setSelectedMonthlyIds(fetched.filter((b) => b.status === 'UNPAID' && b.billType !== 'YEARLY').map((b) => b.id));
    setSelectedYearlyIds(fetched.filter((b) => b.status === 'UNPAID' && b.billType === 'YEARLY').map((b) => b.id));
  };

  const monthlyUnpaidBills = bills.filter((b) => b.status === 'UNPAID' && b.billType !== 'YEARLY');
  const yearlyUnpaidBills = bills.filter((b) => b.status === 'UNPAID' && b.billType === 'YEARLY');

  const selectedMonthlyData = monthlyUnpaidBills.filter((b) => selectedMonthlyIds.includes(b.id));
  const selectedMonthlyTotal = selectedMonthlyData.reduce((sum, b) => sum + (b.totalPayable || 0) + (b.penalty?.amount || 0), 0);
  const selectedMonthlyPenaltyCount = selectedMonthlyData.filter((b) => (b.penalty?.amount || 0) > 0).length;

  const selectedYearlyData = yearlyUnpaidBills.filter((b) => selectedYearlyIds.includes(b.id));
  const selectedYearlyTotal = selectedYearlyData.reduce((sum, b) => sum + (b.totalPayable || 0) + (b.penalty?.amount || 0), 0);

  const toggleMonthly = (id) => {
    setSelectedMonthlyIds((prev) => (prev.includes(id) ? prev.filter((b) => b !== id) : [...prev, id]));
  };
  const toggleYearly = (id) => {
    setSelectedYearlyIds((prev) => (prev.includes(id) ? prev.filter((b) => b !== id) : [...prev, id]));
  };

  const prepareCheckout = async (category, waivePenalty = false) => {
    const billIds = category === 'YEARLY' ? selectedYearlyIds : selectedMonthlyIds;
    if (billIds.length === 0) return;
    setBusy(true);
    setErrorMsg('');
    try {
      const idToken = await auth.currentUser.getIdToken();
      // Waiver offer is monthly-only, capped at 12, and sized to exactly the
      // number of selected bills that currently carry a penalty — matches the
      // proven website logic in ResidentDashboard.js exactly.
      const advMonths = category !== 'YEARLY' && waivePenalty ? Math.min(selectedMonthlyPenaltyCount, 12) : 0;
      const body = {
        houseNo: userRefInfo.plotNo,
        propertyType: userRefInfo.propertyType,
        billIds,
        advanceMonths: advMonths,
        advanceYears: 0,
        waivePenaltyOffer: category !== 'YEARLY' && waivePenalty,
      };
      if (category === 'YEARLY') body.paymentCategory = 'YEARLY';

      const response = await fetch(`${API_BASE_URL}/create-payment-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Could not start checkout.');

      setCheckoutData({ ...data, category, waivePenalty: category !== 'YEARLY' && waivePenalty });
      setStage('confirm');
    } catch (err) {
      setErrorMsg(err.message || 'Could not start checkout. Please try again.');
    }
    setBusy(false);
  };

  const launchRazorpay = () => {
    if (!checkoutData) return;
    setBusy(true);

    const options = {
      key: RAZORPAY_KEY,
      amount: checkoutData.amountInPaise,
      currency: 'INR',
      name: 'Bathinda Housing Complex',
      description: checkoutData.category === 'YEARLY' ? 'Yearly Maintenance Payment' : 'Maintenance Bill Payment',
      order_id: checkoutData.razorpayOrderId,
      handler: async function (response) {
        setStage('verifying');
        try {
          const idToken = await auth.currentUser.getIdToken();
          const verifyRes = await fetch(`${API_BASE_URL}/verify-payment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }),
          });
          const verifyData = await verifyRes.json();
          if (verifyData.success) {
            signalSuccessAndClose();
          } else {
            setErrorMsg(
              verifyData.error ||
                'We could not confirm your payment. If money was deducted, please contact the society office with your payment reference before trying again.'
            );
            setStage('verify-error');
          }
        } catch (err) {
          setErrorMsg(
            'A network error stopped us from confirming your payment. If money was deducted, please contact the society office with your payment reference before trying again.'
          );
          setStage('verify-error');
        }
      },
      modal: {
        ondismiss: function () {
          setBusy(false);
          setStage('confirm');
        },
      },
      prefill: {
        name: profileData?.ownerName || profileData?.name || '',
        contact: profileData?.phone || profileData?.mobile || '',
      },
      theme: { color: '#2b8cee' },
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', function (resp) {
      setBusy(false);
      setErrorMsg(resp?.error?.description || 'Your payment could not be completed. Please try again.');
      setStage('payment-failed');
    });
    setBusy(false);
    rzp.open();
  };

  const backToBills = () => {
    setCheckoutData(null);
    setErrorMsg('');
    setStage('ready');
  };

  // ---------------- RENDER ----------------

  if (stage === 'loading') {
    return (
      <div className="mc-screen mc-center">
        <div className="mc-spinner" />
        <p className="mc-muted">Loading your bills…</p>
      </div>
    );
  }

  if (stage === 'error') {
    return (
      <div className="mc-screen mc-center">
        <div className="mc-icon-circle mc-icon-red">✕</div>
        <h2 className="mc-title">Can't open checkout</h2>
        <p className="mc-muted mc-text-center">{errorMsg}</p>
      </div>
    );
  }

  if (stage === 'success') {
    return (
      <div className="mc-screen mc-center">
        <div className="mc-icon-circle mc-icon-green">✓</div>
        <h2 className="mc-title">Payment Successful</h2>
        <p className="mc-muted mc-text-center">Your ledger has been updated. You can close this screen now.</p>
      </div>
    );
  }

  if (stage === 'verifying') {
    return (
      <div className="mc-screen mc-center">
        <div className="mc-spinner" />
        <p className="mc-muted">Confirming your payment… please don't close this screen.</p>
      </div>
    );
  }

  if (stage === 'verify-error' || stage === 'payment-failed') {
    return (
      <div className="mc-screen mc-center">
        <div className="mc-icon-circle mc-icon-red">✕</div>
        <h2 className="mc-title">{stage === 'payment-failed' ? 'Payment Failed' : 'Payment Not Confirmed'}</h2>
        <p className="mc-muted mc-text-center">{errorMsg}</p>
        <button className="mc-btn mc-btn-primary" onClick={backToBills}>
          Back to Bills
        </button>
      </div>
    );
  }

  if (stage === 'confirm' && checkoutData) {
    const b = checkoutData.breakdown || {};
    const isWaiverOffer = checkoutData.waivePenalty && b.penaltiesWaived > 0;
    return (
      <div className="mc-screen">
        <div className="mc-card">
          <h2 className="mc-title">Confirm Payment</h2>
          <div className="mc-row">
            <span>Selected Bills</span>
            <span>₹{b.baseArrears || 0}</span>
          </div>
          {isWaiverOffer ? (
            <div className="mc-row mc-row-good">
              <span>Penalty Waived</span>
              <span>− ₹{b.penaltiesWaived}</span>
            </div>
          ) : (
            b.penaltiesOriginal > 0 && (
              <div className="mc-row mc-row-warning">
                <span>Penalty</span>
                <span>₹{b.penaltiesOriginal}</span>
              </div>
            )
          )}
          {b.advanceMonthsPaid > 0 && (
            <div className="mc-row">
              <span>{b.advanceMonthsPaid} Advance Month(s)</span>
              <span>₹{b.advanceBaseAmount || 0}</span>
            </div>
          )}
          {b.platformFee > 0 && (
            <div className="mc-row">
              <span>Platform Fee</span>
              <span>₹{b.platformFee}</span>
            </div>
          )}
          <div className="mc-divider" />
          <div className="mc-row mc-row-total">
            <span>Total Payable</span>
            <span>₹{b.finalPayableRupees || 0}</span>
          </div>
          {isWaiverOffer && Array.isArray(b.advanceDetails) && b.advanceDetails.length > 0 && (
            <div className="mc-advance-note">
              This also secures {b.advanceDetails.map((d) => d.month).join(', ')} in advance.
            </div>
          )}
        </div>
        {errorMsg && <p className="mc-muted mc-text-center mc-error-text">{errorMsg}</p>}
        <button className="mc-btn mc-btn-primary" disabled={busy} onClick={launchRazorpay}>
          {busy ? 'Please wait…' : `Pay ₹${b.finalPayableRupees || 0} Now`}
        </button>
        <button className="mc-btn mc-btn-ghost" disabled={busy} onClick={backToBills}>
          Cancel
        </button>
      </div>
    );
  }

  // stage === 'ready'
  return (
    <div className="mc-screen">
      <div className="mc-header">
        <h1 className="mc-h1">
          {profileData?.ownerName || profileData?.name ? `Hi, ${profileData.ownerName || profileData.name}` : 'Your Bills'}
        </h1>
        <p className="mc-muted">
          {formatPropertyType(userRefInfo.propertyType)} #{userRefInfo.plotNo}
        </p>
      </div>

      {errorMsg && <p className="mc-muted mc-text-center mc-error-text">{errorMsg}</p>}

      {monthlyUnpaidBills.length === 0 && yearlyUnpaidBills.length === 0 && (
        <div className="mc-card mc-center">
          <div className="mc-icon-circle mc-icon-green">✓</div>
          <p className="mc-muted mc-text-center">You have no pending dues. Nothing to pay right now.</p>
        </div>
      )}

      {monthlyUnpaidBills.length > 0 && (
        <div className="mc-card">
          <h3 className="mc-section-title">Monthly Dues</h3>
          <div className="mc-bill-list">
            {monthlyUnpaidBills.map((bill) => (
              <label key={bill.id} className="mc-bill-item">
                <input
                  type="checkbox"
                  checked={selectedMonthlyIds.includes(bill.id)}
                  onChange={() => toggleMonthly(bill.id)}
                />
                <div className="mc-bill-info">
                  <span className="mc-bill-month">{bill.month}</span>
                  {bill.penalty?.amount > 0 && <span className="mc-bill-penalty">+₹{bill.penalty.amount} penalty</span>}
                </div>
                <span className="mc-bill-amount">₹{(bill.totalPayable || 0) + (bill.penalty?.amount || 0)}</span>
              </label>
            ))}
          </div>
          <button
            className="mc-btn mc-btn-primary"
            disabled={selectedMonthlyIds.length === 0 || busy}
            onClick={() => prepareCheckout('MONTHLY')}
          >
            {busy ? 'Please wait…' : `Pay Selected (₹${selectedMonthlyTotal})${selectedMonthlyPenaltyCount > 0 ? ' incl. penalty' : ''}`}
          </button>

          {selectedMonthlyPenaltyCount > 0 && (
            <button
              className="mc-btn mc-btn-green"
              disabled={selectedMonthlyIds.length === 0 || busy}
              onClick={() => prepareCheckout('MONTHLY', true)}
            >
              {busy
                ? 'Please wait…'
                : selectedMonthlyPenaltyCount > 12
                ? 'Pay without Penalty + 12 Advance Months (maxed out)'
                : `Pay without Penalty + ${selectedMonthlyPenaltyCount} Advance Month(s)`}
            </button>
          )}
        </div>
      )}

      {yearlyUnpaidBills.length > 0 && (
        <div className="mc-card">
          <h3 className="mc-section-title">Yearly Maintenance</h3>
          <div className="mc-bill-list">
            {yearlyUnpaidBills.map((bill) => (
              <label key={bill.id} className="mc-bill-item">
                <input
                  type="checkbox"
                  checked={selectedYearlyIds.includes(bill.id)}
                  onChange={() => toggleYearly(bill.id)}
                />
                <div className="mc-bill-info">
                  <span className="mc-bill-month">Maintenance ({bill.year})</span>
                  {bill.penalty?.amount > 0 && <span className="mc-bill-penalty">+₹{bill.penalty.amount} penalty</span>}
                </div>
                <span className="mc-bill-amount">₹{(bill.totalPayable || 0) + (bill.penalty?.amount || 0)}</span>
              </label>
            ))}
          </div>
          <button
            className="mc-btn mc-btn-primary"
            disabled={selectedYearlyIds.length === 0 || busy}
            onClick={() => prepareCheckout('YEARLY')}
          >
            {busy ? 'Please wait…' : `Pay Selected (₹${selectedYearlyTotal})`}
          </button>
        </div>
      )}
    </div>
  );
}

export default MobileCheckout;
